/**
 * i18n foundation (#639). Establishes react-i18next so UI strings can be
 * localized instead of hard-coded. Components read strings via the
 * `useTranslation('<namespace>')` hook / `t('key')`; English is the source
 * catalog under `locales/en/`, one JSON file per feature area. Additional
 * locales drop in as `locales/<lng>/` and register in `resources` below.
 *
 * Import this module once for its side effect (done in `src/index.tsx` for the
 * app and `src/setupTests.ts` for tests) before any component calls `t()`.
 */
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enAccount from './locales/en/account.json';
import enAdmin from './locales/en/admin.json';
import enAnalyzer from './locales/en/analyzer.json';
import enAuth from './locales/en/auth.json';
import enBilling from './locales/en/billing.json';
import enCommon from './locales/en/common.json';
import enConvert from './locales/en/convert.json';
import enDashboard from './locales/en/dashboard.json';
import enDebug from './locales/en/debug.json';
import enDevelopers from './locales/en/developers.json';
import enDiscover from './locales/en/discover.json';
import enGdpr from './locales/en/gdpr.json';
import enHistory from './locales/en/history.json';
import enHowto from './locales/en/howto.json';
import enInvitation from './locales/en/invitation.json';
import enJobs from './locales/en/jobs.json';
import enLanding from './locales/en/landing.json';
import enLayout from './locales/en/layout.json';
import enLegal from './locales/en/legal.json';
import enLogs from './locales/en/logs.json';
import enNotifications from './locales/en/notifications.json';
import enOrganization from './locales/en/organization.json';
import enPayment from './locales/en/payment.json';
import enRealtime from './locales/en/realtime.json';
import enRemediation from './locales/en/remediation.json';
import enReports from './locales/en/reports.json';
import enVisualization from './locales/en/visualization.json';
import enWebhooks from './locales/en/webhooks.json';

export const defaultNS = 'common';

export const resources = {
  en: {
    account: enAccount,
    admin: enAdmin,
    analyzer: enAnalyzer,
    auth: enAuth,
    billing: enBilling,
    common: enCommon,
    convert: enConvert,
    dashboard: enDashboard,
    debug: enDebug,
    developers: enDevelopers,
    discover: enDiscover,
    gdpr: enGdpr,
    history: enHistory,
    howto: enHowto,
    invitation: enInvitation,
    jobs: enJobs,
    landing: enLanding,
    layout: enLayout,
    legal: enLegal,
    logs: enLogs,
    notifications: enNotifications,
    organization: enOrganization,
    payment: enPayment,
    realtime: enRealtime,
    remediation: enRemediation,
    reports: enReports,
    visualization: enVisualization,
    webhooks: enWebhooks,
  },
} as const;

// `.use()`/`.init()` is i18next's standard plugin-chaining API on the default
// instance; the import-x caution about the named `use` export is a false positive here.
// eslint-disable-next-line import-x/no-named-as-default-member
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS,
    ns: Object.keys(resources.en),
    // React already escapes interpolated values, so don't double-escape.
    interpolation: { escapeValue: false },
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
    },
    // Resources are bundled synchronously (no lazy backend), so Suspense isn't
    // needed — disabling it keeps rendering (and tests) synchronous.
    react: { useSuspense: false },
  });

// Keep the document's lang attribute in sync with the active language so
// screen readers announce content in the right language (WCAG 3.1.1). The
// detector sets the initial language before React mounts, so seed it once
// here too rather than waiting for the first change.
if (typeof document !== 'undefined') {
  const syncLang = (lng: string) => {
    document.documentElement.lang = lng;
  };
  syncLang(i18n.language || 'en');
  i18n.on('languageChanged', syncLang);
}

export default i18n;
